import React from "react";
import Icons from "../icons";
import { DIALOG_HEIGHT } from "./dialogSize";

/* Xray → Playwright step mapping grid. Mirrors the grid in TestCaseRow so the
   Inbox preview shows the same traceability the board detail does: each
   pre-written Xray step (Action / Data / Expected) alongside the Playwright
   step(s) authored against it (grouped by source_step_no). */
function InboxXrayMappingGrid({ steps, xraySteps }) {
  const byStep = (steps || []).reduce((acc, s) => {
    const k = s.source_step_no ?? 0;
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});

  const renderPwSteps = (arr) => (
    <ul className="space-y-1">
      {arr.map((s, si) => (
        <li key={si} className="flex items-start gap-1.5">
          <span className="mt-1 h-1.5 w-1.5 flex-none rounded-full bg-slate-300" />
          <span className="rounded bg-brand-50 px-1.5 py-0.5 font-mono font-semibold text-brand-700">
            {s.action}
          </span>
          <span className="break-all text-slate-600">
            {s.selector}
            {s.value != null && s.value !== "" ? ` = "${s.value}"` : ""}
          </span>
        </li>
      ))}
    </ul>
  );

  return (
    <div className="overflow-hidden border-t border-violet-100">
      <div className="flex items-center gap-1.5 border-b border-violet-100 bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
        <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] tracking-normal">
          Test
        </span>
        Step mapping (Test step → Artifact)
      </div>
      <table className="w-full table-fixed border-collapse text-left text-xs">
        <thead>
          <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="w-10 p-2">#</th>
            <th className="w-2/5 p-2">Test step (Action / Data / Expected)</th>
            <th className="p-2">Artifact steps</th>
          </tr>
        </thead>
        <tbody>
          {xraySteps.map((xs) => {
            const mapped = byStep[xs.step_no] || [];
            return (
              <tr key={xs.step_no} className="border-t border-slate-100 align-top">
                <td className="p-2 text-slate-400">{xs.step_no}</td>
                <td className="p-2">
                  <div className="text-slate-800">{xs.action}</div>
                  {xs.data ? (
                    <div className="mt-0.5 text-slate-500">
                      <span className="font-medium">Data:</span> {xs.data}
                    </div>
                  ) : null}
                  {xs.result ? (
                    <div className="mt-0.5 text-slate-500">
                      <span className="font-medium">Expected:</span> {xs.result}
                    </div>
                  ) : null}
                </td>
                <td className="p-2">
                  {mapped.length ? (
                    renderPwSteps(mapped)
                  ) : (
                    <span className="italic text-amber-600">No Artifact step found.</span>
                  )}
                </td>
              </tr>
            );
          })}
          {byStep[0]?.length ? (
            <tr className="border-t border-slate-100 bg-slate-50/50 align-top">
              <td className="p-2 text-slate-400">—</td>
              <td className="p-2 italic text-slate-500">Steps not tied to a test step</td>
              <td className="p-2">{renderPwSteps(byStep[0])}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

/* Everything the AI built for one Inbox item, with the decision buttons.
   Extracted from InboxPanel so the Explore & Author run can offer "Approve
   here" without the user hunting for the Inbox in the header — both places
   show the identical review, because approving is a real write to Jira and
   the thing you approve should not look different depending on where you
   happened to be standing.

   `item` is the API shape from inbox_service.out(): title, status,
   link_targets and the full `payload`. `onRemove` is optional — omit it where
   deleting the Inbox record would make no sense. */
export default function InboxItemPreview({
  item,
  busy = false,
  onClose,
  onAccept,
  onDecline,
  onRemove,
}) {
  if (!item) return null;

  const drafts = item.payload?.drafts || [];
  const pwCases = item.payload?.authored_cases || [];
  const isPw = item.kind === "playwright_generation";
  const usage = item.payload?.usage || null;
  const preDrafts = item.payload?.pre_drafts || [];
  const containers = item.payload?.container_drafts || [];
  const pending = item.status === "pending";

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4">
      <div className={`flex ${DIALOG_HEIGHT} w-full max-w-4xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl`}>
        <div className="flex flex-none items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">{item.title}</h3>
            <p className="text-xs text-slate-500">
              {drafts.length} test(s){preDrafts.length ? ` · ${preDrafts.length} shared precondition(s)` : ""}
              {pending ? " — not created yet" : ` — ${item.status}`}
            </p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600">
            <Icons.X className="h-5 w-5" />
          </button>
        </div>

        <div className="cq-scroll min-h-0 flex-1 space-y-4 overflow-y-auto bg-slate-50/60 px-5 py-4">
          {preDrafts.length > 0 && (
            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-sky-700">
                Shared preconditions (1 → many tests)
              </h4>
              <div className="space-y-2">
                {preDrafts.map((p) => {
                  const users = drafts.filter((d) => (d.precondition_names || []).includes(p.name));
                  return (
                    <div key={p.name} className="rounded-xl border border-sky-200 bg-white p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">{p.summary}</span>
                        <span className="rounded-full bg-sky-50 px-2 py-0.5 text-[10px] text-sky-700 ring-1 ring-sky-200">
                          {users.length} test(s)
                        </span>
                      </div>
                      {p.definition && <p className="mt-1 text-xs text-slate-600">{p.definition}</p>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {containers.length > 0 && (
            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
                Will also be created
              </h4>
              <div className="space-y-2">
                {containers.map((c) => (
                  <div key={c.kind} className="flex items-center gap-2 rounded-xl border border-violet-200 bg-white p-3">
                    <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-violet-700 ring-1 ring-violet-200">
                      {c.kind === "test_set" ? "Test Set" : "Test Plan"}
                    </span>
                    <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{c.summary}</span>
                    <span className="flex-none text-[11px] text-slate-500">holds all {drafts.length} test(s)</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          {isPw && usage && usage.calls > 0 && (
            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">
                AI token usage
              </h4>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                {[
                  { label: "LLM calls", value: usage.calls },
                  { label: "Input", value: usage.input },
                  { label: "Output", value: usage.output },
                  { label: "Thinking", value: usage.thinking },
                  { label: "Cached input", value: usage.cached },
                  { label: "Total", value: usage.total },
                ].map((s) => (
                  <div
                    key={s.label}
                    className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center"
                  >
                    <div className="text-sm font-semibold text-slate-800">
                      {(s.value || 0).toLocaleString()}
                    </div>
                    <div className="mt-0.5 text-[10px] uppercase tracking-wider text-slate-400">
                      {s.label}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {isPw && pwCases.length > 0 && (
            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-brand-700">
                Artifacts ({pwCases.length})
              </h4>
              <div className="space-y-4">
                {pwCases.map((c, i) => {
                  const steps = Array.isArray(c.steps) ? c.steps : [];
                  const xraySteps = Array.isArray(c.xray_steps) ? c.xray_steps : [];
                  return (
                    <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                      {/* Case header */}
                      <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2">
                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand-100 text-[10px] font-bold text-brand-700">
                          {i + 1}
                        </span>
                        <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{c.name}</span>
                        <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-500 ring-1 ring-slate-200">
                          {steps.length} step{steps.length !== 1 ? "s" : ""}
                        </span>
                        {c.xray_test_key && (
                          <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700 ring-1 ring-violet-200">
                            {c.xray_test_key}
                          </span>
                        )}
                      </div>

                      {xraySteps.length > 0 ? (
                        <InboxXrayMappingGrid steps={steps} xraySteps={xraySteps} />
                      ) : (
                        <ol className="divide-y divide-slate-50">
                          {steps.map((s, si) => (
                            <li key={si} className="flex items-start gap-2 px-3 py-1.5 text-xs text-slate-600">
                              <span className="mt-px flex h-4 w-4 flex-none items-center justify-center rounded bg-slate-100 text-[9px] font-semibold text-slate-500">
                                {si + 1}
                              </span>
                              <span className="min-w-0 flex-1">
                                <span className="rounded bg-brand-50 px-1.5 py-0.5 font-mono font-semibold text-brand-700">
                                  {s.action}
                                </span>
                                {s.selector && <span className="ml-1 break-all text-slate-600">{s.selector}</span>}
                                {s.value != null && s.value !== "" && (
                                  <span className="ml-1 text-slate-600">= "{s.value}"</span>
                                )}
                              </span>
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {drafts.length > 0 && (
            <section>
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Tests</h4>
              <div className="space-y-3">
                {drafts.map((d, i) => (
                  <div key={i} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
                    <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold text-slate-600">{i + 1}</span>
                      <span className="min-w-0 flex-1 text-sm font-semibold text-slate-800">{d.summary}</span>
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-slate-500 ring-1 ring-slate-200">{d.project_key}</span>
                      {d.test_set_key && (
                        <span className="rounded-full bg-violet-50 px-2 py-0.5 text-[10px] text-violet-700 ring-1 ring-violet-200">set {d.test_set_key}</span>
                      )}
                    </div>
                    {(d.precondition_summary || (d.precondition_names || []).length > 0) && (
                      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-100 bg-sky-50/40 px-3 py-1.5 text-[11px]">
                        <span className="font-semibold uppercase tracking-wider text-sky-700">Preconditions</span>
                        {(d.precondition_names || []).map((n) => (
                          <span key={n} className="rounded bg-white px-1.5 py-0.5 ring-1 ring-sky-200">{n} <span className="text-sky-600">(shared)</span></span>
                        ))}
                        {d.precondition_summary && (
                          <span className="rounded bg-white px-1.5 py-0.5 ring-1 ring-slate-200">{d.precondition_summary} <span className="text-slate-400">(this test)</span></span>
                        )}
                      </div>
                    )}
                    <table className="w-full table-fixed text-left text-xs">
                      <thead>
                        <tr className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                          <th className="w-10 px-3 py-2">#</th>
                          <th className="px-3 py-2">Action</th>
                          <th className="w-1/4 px-3 py-2">Data</th>
                          <th className="w-1/3 px-3 py-2">Expected result</th>
                        </tr>
                      </thead>
                      <tbody className="align-top">
                        {(d.steps || []).map((s, si) => (
                          <tr key={si} className="border-t border-slate-100">
                            <td className="px-3 py-2 text-slate-400">{si + 1}</td>
                            <td className="px-3 py-2 text-slate-800">{s.action || "—"}</td>
                            <td className="px-3 py-2 text-slate-500">{s.data || "—"}</td>
                            <td className="px-3 py-2 text-slate-600">{s.result || "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        <div className="flex flex-none items-center justify-between gap-2 border-t border-slate-200 px-5 py-3">
          <p className="text-[11px] text-slate-500">
            On accept: preconditions → tests → test set → linked to {(item.link_targets || []).join(", ") || "the ticket"}.
          </p>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">Close</button>
            {pending && (
              <>
                <button type="button" onClick={onDecline} disabled={busy}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50 disabled:opacity-50">
                  Decline
                </button>
                <button type="button" onClick={onAccept} disabled={busy}
                  className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                  {busy ? <Icons.Spinner className="h-4 w-4 animate-spin" /> : <Icons.Check className="h-4 w-4" />}
                  Accept &amp; create
                </button>
              </>
            )}
            {!pending && onRemove && (
              <button type="button" onClick={onRemove} disabled={busy}
                className="inline-flex items-center gap-2 rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-50">
                {busy ? (
                  <Icons.Spinner className="h-4 w-4 animate-spin" />
                ) : (
                  <Icons.Trash className="h-4 w-4" />
                )}
                Delete from Inbox
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
